import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Tags, Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, Search,
  Download, FolderTree, CheckCircle2, AlertTriangle, Tag,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { PP_DOMAINS } from "../lib/constants";
import { useTranslation } from "react-i18next";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";

const NONE = "__none__";
const normKey = (s) => (s || "").toString().trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export default function CategoriesActivites() {
  const { t } = useTranslation();
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [cats, setCats] = useState([]);
  const [acts, setActs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [onlyUnclassified, setOnlyUnclassified] = useState(searchParams.get("filter") === "unclassified");

  // dialog état
  const [editOpen, setEditOpen] = useState(false);
  const [editCat, setEditCat] = useState(null); // null = création
  const [form, setForm] = useState({ name_fr: "", name_nl: "", active: true, domains: [] });
  const [saving, setSaving] = useState(false);
  const [actTag, setActTag] = useState(null); // activité en cours de tagging
  const [tagSaving, setTagSaving] = useState(false);

  const loadGroups = async () => {
    try {
      const { data } = await api.get("/planning/catalog/groups");
      const gs = data.groups || [];
      setGroups(gs);
      if (gs.length && !groupId) setGroupId(gs[0].id);
      if (!gs.length) setLoading(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setLoading(false); }
  };

  const loadCatalog = async (gid) => {
    if (!gid) return;
    setLoading(true);
    try {
      const { data } = await api.get("/planning/catalog", { params: { group_id: gid } });
      const cs = (data.categories || []).slice().sort((a, b) => (a.order - b.order) || a.name_fr.localeCompare(b.name_fr));
      setCats(cs);
      setActs(data.activities || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setCats([]); setActs([]); }
    setLoading(false);
  };

  useEffect(() => { loadGroups(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (groupId) loadCatalog(groupId); /* eslint-disable-next-line */ }, [groupId]);

  const activeCats = useMemo(() => cats.filter((c) => c.active), [cats]);
  const catById = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);
  const unclassifiedCount = useMemo(() => acts.filter((a) => !a.category_id).length, [acts]);

  const shownActs = useMemo(() => {
    const q = normKey(search);
    return acts.filter((a) => {
      if (onlyUnclassified && a.category_id) return false;
      if (!q) return true;
      return normKey(a.name_fr).includes(q) || normKey(a.name_nl).includes(q);
    });
  }, [acts, search, onlyUnclassified]);

  // ── Catégories ──
  const openCreate = () => { setEditCat(null); setForm({ name_fr: "", name_nl: "", active: true, domains: [] }); setEditOpen(true); };
  const openEdit = (c) => { setEditCat(c); setForm({ name_fr: c.name_fr || "", name_nl: c.name_nl || "", active: c.active !== false, domains: c.domains || [] }); setEditOpen(true); };

  const saveCat = async () => {
    if (!form.name_fr.trim()) { toast.error("Le nom (FR) est obligatoire."); return; }
    setSaving(true);
    try {
      if (editCat) {
        const { data } = await api.put(`/planning/catalog/categories/${editCat.id}`, {
          group_id: groupId, name_fr: form.name_fr.trim(), name_nl: form.name_nl.trim(),
          active: form.active, order: editCat.order || 0, domains: form.domains || [],
        });
        setCats((prev) => prev.map((c) => (c.id === editCat.id ? { ...c, ...data } : c)));
        toast.success("Catégorie mise à jour.");
      } else {
        const { data } = await api.post("/planning/catalog/categories", {
          group_id: groupId, name_fr: form.name_fr.trim(), name_nl: form.name_nl.trim(),
          active: form.active, order: cats.length, domains: form.domains || [],
        });
        setCats((prev) => [...prev, data]);
        toast.success("Catégorie créée.");
      }
      setEditOpen(false);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setSaving(false);
  };

  const openActTags = (a) => setActTag({
    id: a.id, name_fr: a.name_fr, name_nl: a.name_nl, category_id: a.category_id,
    default_image_id: a.default_image_id, active: a.active,
    domains: a.domains || [], keywords: (a.keywords || []).join(", "),
    inherit_domains: a.inherit_domains !== false,
    objectives: (a.objectives || []).join("\n"),
  });
  const toggleTagDomain = (d) => setActTag((t) => ({ ...t, domains: t.domains.includes(d) ? t.domains.filter((x) => x !== d) : [...t.domains, d] }));
  const saveActTags = async () => {
    setTagSaving(true);
    try {
      const kws = actTag.keywords.split(",").map((s) => s.trim()).filter(Boolean);
      const objs = (actTag.objectives || "").split("\n").map((s) => s.trim()).filter(Boolean);
      const { data } = await api.put(`/planning/catalog/activities/${actTag.id}`, {
        group_id: groupId, name_fr: actTag.name_fr, name_nl: actTag.name_nl || "",
        category_id: actTag.category_id || null, default_image_id: actTag.default_image_id || "",
        active: actTag.active, domains: actTag.domains, keywords: kws, inherit_domains: actTag.inherit_domains,
        objectives: objs,
      });
      setActs((prev) => prev.map((a) => (a.id === actTag.id ? { ...a, ...data } : a)));
      toast.success(t("pp.tag.tagsSaved", { defaultValue: "Tags enregistrés" }));
      setActTag(null);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    setTagSaving(false);
  };

  const toggleActive = async (c) => {
    try {
      const { data } = await api.put(`/planning/catalog/categories/${c.id}`, {
        group_id: groupId, name_fr: c.name_fr, name_nl: c.name_nl, active: !c.active, order: c.order || 0,
      });
      setCats((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...data } : x)));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const deleteCat = async (c) => {
    const used = acts.filter((a) => a.category_id === c.id).length;
    if (!window.confirm(`Supprimer la catégorie « ${c.name_fr} » ?${used ? `\n${used} activité(s) rattachée(s) seront déclassées.` : ""}`)) return;
    try {
      await api.delete(`/planning/catalog/categories/${c.id}`);
      setCats((prev) => prev.filter((x) => x.id !== c.id));
      setActs((prev) => prev.map((a) => (a.category_id === c.id ? { ...a, category_id: null } : a)));
      toast.success("Catégorie supprimée.");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= cats.length) return;
    const a = cats[idx], b = cats[j];
    const oa = a.order ?? idx, ob = b.order ?? j;
    const next = cats.map((c) => (c.id === a.id ? { ...a, order: ob } : c.id === b.id ? { ...b, order: oa } : c));
    next.sort((x, y) => (x.order - y.order) || x.name_fr.localeCompare(y.name_fr));
    setCats(next);
    try {
      await Promise.all([
        api.put(`/planning/catalog/categories/${a.id}`, { group_id: groupId, name_fr: a.name_fr, name_nl: a.name_nl, active: a.active, order: ob }),
        api.put(`/planning/catalog/categories/${b.id}`, { group_id: groupId, name_fr: b.name_fr, name_nl: b.name_nl, active: b.active, order: oa }),
      ]);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); loadCatalog(groupId); }
  };

  const seedBase = async () => {
    if (!window.confirm("Importer la base de départ (thèmes + activités) pour ce groupe ?")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/planning/catalog/seed", null, { params: { group_id: groupId } });
      toast.success(`Base importée : ${data.created} thème(s) + ${data.activities_created} activité(s).`);
      await loadCatalog(groupId);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); setLoading(false); }
  };

  // ── Activités : rattachement ──
  const assign = async (act, value) => {
    const category_id = value === NONE ? "" : value;
    setActs((prev) => prev.map((a) => (a.id === act.id ? { ...a, category_id: category_id || null } : a)));
    try {
      await api.put(`/planning/catalog/activities/${act.id}/category`, { category_id });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
      setActs((prev) => prev.map((a) => (a.id === act.id ? { ...a, category_id: act.category_id } : a)));
    }
  };

  return (
    <div className="min-h-screen bg-[#0b1620] text-slate-100 -m-4 sm:-m-6 p-4 sm:p-8" data-testid="categories-activites-page">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-teal-400"><Tags className="h-5 w-5" /><span className="text-xs font-semibold uppercase tracking-widest">CarePilot · Administration</span></div>
            <h1 className="text-3xl sm:text-4xl font-extrabold mt-1">Catégories d'activités</h1>
            <p className="text-slate-400 text-sm mt-1">Gérez les catégories et rattachez les activités du catalogue pour alimenter le tableau de bord « Vie sociale ».</p>
          </div>
          {groups.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-400 uppercase tracking-wide">Groupe</span>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger className="w-56 bg-white/[0.04] border-white/10 text-slate-100" data-testid="cat-group-select"><SelectValue /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Chargement…</div>
        ) : !groups.length ? (
          <div className="rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-10 text-center text-slate-400">Aucun groupe enregistré.</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Colonne catégories */}
            <div className="lg:col-span-2 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2"><FolderTree className="h-4 w-4 text-teal-400" /> Catégories ({cats.length})</h3>
                <div className="flex items-center gap-2">
                  {!cats.length && <Button size="sm" variant="outline" onClick={seedBase} className="border-white/15 text-slate-200" data-testid="cat-seed-btn"><Download className="h-4 w-4 mr-1" />Importer la base</Button>}
                  <Button size="sm" onClick={openCreate} className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold" data-testid="cat-add-btn"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>
                </div>
              </div>
              {!cats.length ? (
                <p className="text-sm text-slate-400 py-6 text-center">Aucune catégorie. Importez la base ou ajoutez-en une.</p>
              ) : (
                <div className="space-y-2" data-testid="cat-list">
                  {cats.map((c, idx) => {
                    const used = acts.filter((a) => a.category_id === c.id).length;
                    return (
                      <motion.div key={c.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        className="flex items-center gap-2 rounded-xl bg-white/[0.03] ring-1 ring-white/10 px-3 py-2" data-testid={`cat-row-${c.id}`}>
                        <div className="flex flex-col">
                          <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-slate-500 hover:text-teal-300 disabled:opacity-20"><ArrowUp className="h-3.5 w-3.5" /></button>
                          <button onClick={() => move(idx, 1)} disabled={idx === cats.length - 1} className="text-slate-500 hover:text-teal-300 disabled:opacity-20"><ArrowDown className="h-3.5 w-3.5" /></button>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className={`text-sm font-medium truncate ${c.active ? "text-slate-100" : "text-slate-500 line-through"}`}>{c.name_fr}</div>
                          <div className="text-xs text-slate-500 truncate">{c.name_nl || "—"} · {used} activité{used > 1 ? "s" : ""}</div>
                        </div>
                        <Switch checked={c.active} onCheckedChange={() => toggleActive(c)} data-testid={`cat-active-${c.id}`} />
                        <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-sky-300 p-1" data-testid={`cat-edit-${c.id}`}><Pencil className="h-4 w-4" /></button>
                        <button onClick={() => deleteCat(c)} className="text-slate-400 hover:text-red-400 p-1" data-testid={`cat-del-${c.id}`}><Trash2 className="h-4 w-4" /></button>
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Colonne activités */}
            <div className="lg:col-span-3 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <h3 className="text-sm font-semibold text-slate-200">Activités du catalogue ({acts.length})</h3>
                <div className="flex items-center gap-2 text-xs">
                  {unclassifiedCount > 0
                    ? <span className="inline-flex items-center gap-1 text-amber-300"><AlertTriangle className="h-3.5 w-3.5" />{unclassifiedCount} non classée{unclassifiedCount > 1 ? "s" : ""}</span>
                    : <span className="inline-flex items-center gap-1 text-emerald-300"><CheckCircle2 className="h-3.5 w-3.5" />Tout est classé</span>}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher une activité…" className="pl-9 bg-white/[0.04] border-white/10 text-slate-100" data-testid="cat-act-search" />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                  <Switch checked={onlyUnclassified} onCheckedChange={setOnlyUnclassified} data-testid="cat-act-filter-unclassified" />
                  Non classées uniquement
                </label>
              </div>
              {!acts.length ? (
                <p className="text-sm text-slate-400 py-8 text-center">Aucune activité dans le catalogue de ce groupe. Importez la base pour démarrer.</p>
              ) : (
                <div className="max-h-[62vh] overflow-y-auto pr-1 space-y-1.5" data-testid="cat-act-list">
                  {shownActs.map((a) => (
                    <div key={a.id} className="flex items-center gap-3 rounded-xl bg-white/[0.03] ring-1 ring-white/10 px-3 py-2" data-testid={`cat-act-row-${a.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-slate-100 truncate">{a.name_fr}</div>
                        {(a.domains?.length > 0 || a.keywords?.length > 0) ? (
                          <div className="flex flex-wrap gap-1 mt-0.5">
                            {(a.domains || []).slice(0, 3).map((d) => <span key={d} className="rounded-full bg-teal-500/15 text-teal-300 px-1.5 py-0.5 text-[10px]">{d}</span>)}
                            {(a.keywords || []).slice(0, 3).map((k) => <span key={k} className="rounded-full bg-amber-500/15 text-amber-300 px-1.5 py-0.5 text-[10px]">{k}</span>)}
                          </div>
                        ) : a.name_nl && <div className="text-xs text-slate-500 truncate">{a.name_nl}</div>}
                      </div>
                      <button onClick={() => openActTags(a)} data-testid={`cat-act-tags-${a.id}`} title={t("pp.tag.tagActivity")}
                        className="shrink-0 p-2 rounded-lg text-slate-400 hover:text-teal-300 hover:bg-white/5"><Tag className="h-4 w-4" /></button>
                      <Select value={a.category_id || NONE} onValueChange={(v) => assign(a, v)}>
                        <SelectTrigger className={`w-44 shrink-0 text-xs border-white/10 ${a.category_id ? "bg-teal-500/10 text-teal-200" : "bg-white/[0.04] text-slate-400"}`} data-testid={`cat-act-select-${a.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={NONE}>— Non classé —</SelectItem>
                          {activeCats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name_fr}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                  {!shownActs.length && <p className="text-sm text-slate-500 py-6 text-center">Aucune activité ne correspond.</p>}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dialog catégorie */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100" data-testid="cat-dialog">
          <DialogHeader><DialogTitle>{editCat ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="text-xs text-slate-400">Nom (FR) *</Label>
              <Input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10 text-slate-100" data-testid="cat-form-fr" />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Nom (NL)</Label>
              <Input value={form.name_nl} onChange={(e) => setForm({ ...form, name_nl: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10 text-slate-100" data-testid="cat-form-nl" />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="cat-form-active" /> Catégorie active
            </label>
            <div>
              <Label className="text-xs text-slate-400">{t("pp.tag.catDomains")}</Label>
              <p className="text-[11px] text-slate-500 mb-1.5">{t("pp.tag.catHint")}</p>
              <div className="flex flex-wrap gap-1.5" data-testid="cat-domains">
                {PP_DOMAINS.map((d) => {
                  const on = (form.domains || []).includes(d);
                  return (
                    <button key={d} type="button" onClick={() => setForm((f) => ({ ...f, domains: on ? f.domains.filter((x) => x !== d) : [...(f.domains || []), d] }))}
                      className={`rounded-full px-2.5 py-1 text-xs transition ${on ? "bg-teal-500 text-slate-900 font-semibold" : "bg-white/[0.06] text-slate-300 hover:bg-white/10"}`}>{d}</button>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)} className="border-white/15 text-slate-200">Annuler</Button>
            <Button onClick={saveCat} disabled={saving} className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold" data-testid="cat-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog tags activité (domaines + mots-clés + héritage) */}
      <Dialog open={!!actTag} onOpenChange={(o) => { if (!o) setActTag(null); }}>
        <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100 max-w-lg" data-testid="act-tag-dialog">
          <DialogHeader><DialogTitle>{t("pp.tag.tagActivity")} — {actTag?.name_fr}</DialogTitle></DialogHeader>
          {actTag && (
            <div className="space-y-4 py-2">
              <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                <Switch checked={actTag.inherit_domains} onCheckedChange={(v) => setActTag({ ...actTag, inherit_domains: v })} data-testid="act-tag-inherit" /> {t("pp.tag.inherit")}
              </label>
              <div>
                <Label className="text-xs text-slate-400">{t("pp.tag.domains")}</Label>
                <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="act-tag-domains">
                  {PP_DOMAINS.map((d) => {
                    const on = actTag.domains.includes(d);
                    return (
                      <button key={d} type="button" onClick={() => toggleTagDomain(d)}
                        className={`rounded-full px-2.5 py-1 text-xs transition ${on ? "bg-teal-500 text-slate-900 font-semibold" : "bg-white/[0.06] text-slate-300 hover:bg-white/10"}`}>{d}</button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("pp.tag.keywords")}</Label>
                <Input value={actTag.keywords} onChange={(e) => setActTag({ ...actTag, keywords: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10 text-slate-100" data-testid="act-tag-keywords" placeholder="marche, extérieur, lien social" />
                <p className="text-[11px] text-slate-500 mt-1">{t("pp.tag.keywordsHint")}</p>
              </div>
              <div>
                <Label className="text-xs text-slate-400">{t("pp.tag.objectives")}</Label>
                <Textarea value={actTag.objectives} onChange={(e) => setActTag({ ...actTag, objectives: e.target.value })} rows={5} className="mt-1 bg-white/[0.04] border-white/10 text-slate-100" data-testid="act-tag-objectives" placeholder={t("pp.tag.objectivesPh")} />
                <p className="text-[11px] text-slate-500 mt-1">{t("pp.tag.objectivesHint")}</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setActTag(null)} className="border-white/15 text-slate-200">Annuler</Button>
            <Button onClick={saveActTags} disabled={tagSaving} className="bg-teal-500 hover:bg-teal-400 text-slate-900 font-semibold" data-testid="act-tag-save">
              {tagSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("pp.tag.saveTags")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
